import { motion } from "framer-motion";
import { Sparkles, LucideIcon } from "lucide-react";
import { FavoriteButton } from "@features/tools/components/favorite-button";

interface ToolCardProps {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconBgColor: string;
  onClick: () => void;
  onToggleFavorite?: (e: React.MouseEvent) => void;
  isFavorite?: boolean;
  isNew?: boolean;
  disabled?: boolean;
  index?: number;
}

const ToolCard = ({
  id,
  title,
  description,
  icon: Icon,
  iconBgColor,
  onClick,
  onToggleFavorite,
  isFavorite = false,
  isNew = false,
  disabled = false,
  index = 0,
}: ToolCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="group relative"
    >
      <div
        onClick={onClick}
        className={`
          relative overflow-hidden cursor-pointer
          bg-white dark:bg-dark-500
          border border-gray-200/60 dark:border-dark-400
          rounded-2xl 
          transition-all duration-300 ease-out
          hover:shadow-none
          backdrop-blur-sm
          flex h-32
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        {/* Cam yansıması efekti */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
        </div>

        {/* New badge */}
        {isNew && (
          <div className="absolute top-0 right-0 z-20">
            <div className="bg-primary-500 text-white text-xs font-medium px-2.5 py-1 rounded-bl-md flex items-center gap-1">
              <Sparkles size={10} />
              New
            </div>
          </div>
        )}

        {/* Favorite button */}
        {onToggleFavorite && (
          <div className="absolute top-3 right-3 z-20">
            <div
              onClick={onToggleFavorite}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <FavoriteButton
                isFavorite={isFavorite}
                onToggleFavorite={() => {}}
              />
            </div>
          </div>
        )}

        {/* Sol taraf - Icon bölümü */}
        <div
          className={`
          w-24 flex items-center justify-center
          rounded-l-2xl
          relative overflow-hidden
          transition-all duration-300
          ${iconBgColor}
        `}
        >
          <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
            <Icon size={28} className="text-white" />
          </div>
        </div>

        {/* Sağ taraf - Content bölümü */}
        <div className="flex-1 p-4 flex flex-col justify-center relative dark:bg-dark-600">
          <div className="relative z-10 space-y-2">
            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-50 transition-colors duration-200 line-clamp-2 leading-tight">
              {title}
            </h3>

            {/* Description */}
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ToolCard;
